import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { petAPI, clinicAPI } from "../../services/api";
import { showErrorToast, showSuccessToast } from "../../utils/notificationUtils";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { useFlatpickrUserType } from "../../hooks/useFlatpickrUserType";
import { useTranslation } from "react-i18next";

const AppointmentModal = ({
  isOpen,
  onClose,
  onSave,
  vets = [],
  workingHours = [],
  appointmentTypes = [],
  isAppointments = true,
  initialVet = null,
  initialPetOwner = null,
  initialPet = null,
}) => {
  // Apply user type color to Flatpickr calendars globally
  useFlatpickrUserType();
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "ar" ? "ar-EG" : "en-US";
  const fallbackLabels = useMemo(
    () => ({
      petGuardian: t("receptionAppointments.create.fallback.petGuardian"),
      pet: t("receptionAppointments.create.fallback.pet"),
      vet: t("receptionAppointments.create.fallback.vet"),
      noVisits: t("receptionAppointments.create.fields.lastVisit.noVisits"),
    }),
    [t]
  );

  const getVetDisplayName = useCallback(
    (vet) => {
      if (!vet) return "";
      return (
        vet.user?.name ||
        vet.name ||
        t("receptionAppointments.create.fields.vet.generic", {
          id: vet.user_id,
        })
      );
    },
    [t]
  );

  const [petOwnerSearch, setPetOwnerSearch] = useState("");
  const [selectedPetOwner, setSelectedPetOwner] = useState(null);
  const [petOwnerPets, setPetOwnerPets] = useState([]);
  const [isLoadingPets, setIsLoadingPets] = useState(false);
  const [petOwnerSearchResults, setPetOwnerSearchResults] = useState([]);
  const [showPetOwnerDropdown, setShowPetOwnerDropdown] = useState(false);
  const [isSearchingPetOwners, setIsSearchingPetOwners] = useState(false);
  const [vetSearch, setVetSearch] = useState("");
  const [selectedVet, setSelectedVet] = useState(null);
  const [showVetDropdown, setShowVetDropdown] = useState(false);
  const [selectedPet, setSelectedPet] = useState(null);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);
  const [apiAvailabilityData, setApiAvailabilityData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [newAppointment, setNewAppointment] = useState({
    id: null,
    pet_owner: "",
    pet: "",
    vet: "",
    reason: "",
    date: "",
    time: "",
    type: "",
  });

  // Refs for Flatpickr
  const dateInputRef = useRef(null);
  const timeInputRef = useRef(null);
  const datePickerRef = useRef(null);
  const timePickerRef = useRef(null);
  const selectedVetRef = useRef(selectedVet);
  const isInitializedRef = useRef(false);

  // Normalize various possible day formats to 3-letter uppercase (e.g., MON)
  const normalizeDay = (dayValue) => {
    if (!dayValue) return null;
    const value = dayValue.toString().trim();
    const map = {
      sunday: 'SUN', sun: 'SUN',
      monday: 'MON', mon: 'MON',
      tuesday: 'TUE', tue: 'TUE', tues: 'TUE',
      wednesday: 'WED', wed: 'WED',
      thursday: 'THU', thu: 'THU', thur: 'THU', thurs: 'THU',
      friday: 'FRI', fri: 'FRI',
      saturday: 'SAT', sat: 'SAT',
      SUN: 'SUN', MON: 'MON', TUE: 'TUE', WED: 'WED', THU: 'THU', FRI: 'FRI', SAT: 'SAT'
    };
    const key = value.toLowerCase();
    if (map[key]) return map[key];
    return value.length >= 3 ? value.slice(0, 3).toUpperCase() : null;
  };

  // Generate time slots based on working hours (robust to different shapes)
  const generateTimeSlots = (workingHours) => {
    if (!Array.isArray(workingHours) || workingHours.length === 0) return [];

    const slots = [];
    for (const wh of workingHours) {
      const enabled = wh.enabled === undefined ? true : (wh.enabled === true || wh.enabled === 1 || wh.enabled === '1');
      if (!enabled) continue;

      const day = normalizeDay(wh.day);
      const startTime = wh.opening_time || wh.open_time || wh.from || wh.start || wh.start_time;
      const endTime = wh.closing_time || wh.close_time || wh.to || wh.end || wh.end_time;

      if (!day || !startTime || !endTime) continue;

      const startMinutes = timeToMinutes(startTime);
      const endMinutes = timeToMinutes(endTime);
      if (startMinutes === null || endMinutes === null || startMinutes >= endMinutes) continue;

      for (let minutes = startMinutes; minutes < endMinutes; minutes += 30) {
        const timeString = minutesToTime(minutes);
        slots.push({ time: timeString, display: timeString, day });
      }
    }
    return slots.sort((a, b) => a.time.localeCompare(b.time));
  };

  // Helper functions for time conversion
  const timeToMinutes = (timeString) => {
    if (!timeString || typeof timeString !== 'string' || !timeString.includes(':')) return null;
    const [hoursStr, minutesStr] = timeString.split(':');
    const hours = Number(hoursStr);
    const minutes = Number(minutesStr);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
    return hours * 60 + minutes;
  };

  const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // When Pet Guardian changes, fetch their pets
  useEffect(() => {
    if (selectedPetOwner) {
      // Use pets from the search response if available, otherwise fall back to API call
      const petsFromSearch = selectedPetOwner.pets?.list || selectedPetOwner.pets || [];

      if (petsFromSearch.length > 0) {
        // Use pets from search response (no API call needed)
        setPetOwnerPets(petsFromSearch);

        // Auto-select initial pet if provided (for SOS conversations), otherwise first pet if only one
        if (initialPet) {
          const foundPet = petsFromSearch.find(pet => String(pet.id) === String(initialPet.id));
          if (foundPet) {
            setSelectedPet(foundPet);
          } else if (petsFromSearch.length === 1) {
            setSelectedPet(petsFromSearch[0]);
          } else {
            // Even if not found in list, use initialPet if provided (it might be a new pet not yet in list)
            setSelectedPet(initialPet);
          }
        } else if (petsFromSearch.length === 1) {
          setSelectedPet(petsFromSearch[0]);
        } else {
          setSelectedPet(null);
        }
        setIsLoadingPets(false);
      } else {
        // Fallback: fetch pets via API (for cases where search didn't include pets)
        setIsLoadingPets(true);
        petAPI.getOtherPets(selectedPetOwner.id)
          .then((res) => {
            const pets = res.data?.data || [];
            setPetOwnerPets(pets);

            // Auto-select initial pet if provided (for SOS conversations), otherwise first pet if only one
            if (initialPet) {
              const foundPet = pets.find(pet => String(pet.id) === String(initialPet.id));
              if (foundPet) {
                setSelectedPet(foundPet);
              } else if (pets.length === 1) {
                setSelectedPet(pets[0]);
              } else {
                // Even if not found in list, use initialPet if provided (it might be a new pet not yet in list)
                setSelectedPet(initialPet);
              }
            } else if (pets.length === 1) {
              setSelectedPet(pets[0]);
            } else {
              setSelectedPet(null);
            }
          })
          .catch(() => {
            setPetOwnerPets([]);
            setSelectedPet(null);
          })
          .finally(() => {
            setIsLoadingPets(false);
          });
      }
    } else {
      setPetOwnerPets([]);
      setSelectedPet(null);
      setIsLoadingPets(false);
    }
  }, [selectedPetOwner?.id, initialPet]);

  // Pet Guardian search with debouncing
  useEffect(() => {
    if (!petOwnerSearch.trim()) {
      setPetOwnerSearchResults([]);
      setIsSearchingPetOwners(false);
      return;
    }
    setIsSearchingPetOwners(true);
    const timeoutId = setTimeout(() => {
      clinicAPI.searchGuardians(petOwnerSearch)
        .then((response) => {
          setPetOwnerSearchResults(response.data?.data || []);
          setIsSearchingPetOwners(false);
        })
        .catch((error) => {
          console.error('Error searching Pet Guardians:', error);
          setPetOwnerSearchResults([]);
          setIsSearchingPetOwners(false);
        });
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [petOwnerSearch]);

  // Generate time slots when working hours change
  useEffect(() => {
    const slots = generateTimeSlots(workingHours);
    setAvailableTimeSlots(slots);
  }, [workingHours]);

  // Reset API availability data when vet selection changes
  useEffect(() => {
    setApiAvailabilityData(null);
    setIsLoadingAvailability(false);
  }, [selectedVet]);

  // Keep selectedVetRef updated
  useEffect(() => {
    selectedVetRef.current = selectedVet;
  }, [selectedVet]);

  // Get available time slots from API data or fallback to working hours
  const getAvailableTimeSlotsForDate = (selectedDate) => {
    if (!selectedDate) {
      return [];
    }

    // If we have API availability data for the selected date, use it
    if (apiAvailabilityData && apiAvailabilityData.availability) {
      const availability = apiAvailabilityData.availability;

      // Check if clinic is open and we have available slots
      if (!availability.is_clinic_open || !availability.available_slots) {
        return [];
      }

      const selectedDateObj = new Date(selectedDate);
      const today = new Date();
      const isToday = selectedDateObj.toDateString() === today.toDateString();

      // Filter available slots
      let availableSlots = availability.available_slots.filter(slot => slot.is_available);

      // If it's today, filter out past time slots
      if (isToday) {
        const currentHour = today.getHours();
        const currentMinute = today.getMinutes();
        const currentTimeInMinutes = currentHour * 60 + currentMinute;

        availableSlots = availableSlots.filter(slot => {
          const [slotHour, slotMinute] = slot.start_time.split(':').map(Number);
          const slotTimeInMinutes = slotHour * 60 + slotMinute;

          // Show time slots that are at least 15 minutes in the future
          return slotTimeInMinutes >= (currentTimeInMinutes + 15);
        });
      }

      // Transform API slots to match the expected format
      return availableSlots.map(slot => ({
        time: slot.start_time,
        display: slot.start_time,
        endTime: slot.end_time,
        day: availability.day_name?.substring(0, 3).toUpperCase() || 'DAY',
        isAvailable: slot.is_available
      }));
    }

    // Fallback to working hours-based slots if no API data
    const selectedDay = new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
    const selectedDateObj = new Date(selectedDate);
    const today = new Date();
    const isToday = selectedDateObj.toDateString() === today.toDateString();

    let filteredSlots = availableTimeSlots.filter(slot => slot.day === selectedDay);

    if (isToday) {
      const currentHour = today.getHours();
      const currentMinute = today.getMinutes();
      const currentTimeInMinutes = currentHour * 60 + currentMinute;

      filteredSlots = filteredSlots.filter(slot => {
        const [slotHour, slotMinute] = slot.time.split(':').map(Number);
        const slotTimeInMinutes = slotHour * 60 + slotMinute;
        return slotTimeInMinutes >= (currentTimeInMinutes + 15);
      });
    }

    return filteredSlots;
  };

  // Initialize Flatpickr for date picker only
  useEffect(() => {
    if (isOpen && dateInputRef.current) {
      // Initialize date picker
      if (datePickerRef.current) {
        datePickerRef.current.destroy();
      }

      // Create array of available days based on working hours
      const availableDays = workingHours.map(wh => {
        const dayMap = {
          'sunday': 0, 'monday': 1, 'tuesday': 2, 'wednesday': 3,
          'thursday': 4, 'friday': 5, 'saturday': 6
        };
        return dayMap[wh.day] || 0;
      });
      // Enable working hours restriction and disable past dates
      const disableFunction = workingHours.length > 0 ? [
        function (date) {
          // Disable past dates
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (date < today) {
            return true;
          }

          // Disable dates that are not in working hours
          const dayOfWeek = date.getDay();
          const isDisabled = !availableDays.includes(dayOfWeek);
          return isDisabled;
        }
      ] : [
        function (date) {
          // Disable past dates when no working hours are set
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const isPastDate = date < today;
          return isPastDate;
        }
      ];

      datePickerRef.current = flatpickr(dateInputRef.current, {
        dateFormat: "Y-m-d",
        minDate: "today",
        disable: disableFunction,
        onChange: function (selectedDates, dateStr) {
          setNewAppointment(prev => ({ ...prev, date: dateStr, time: "" })); // Reset time when date changes

          // Call vet availability API if vet is selected
          const currentSelectedVet = selectedVetRef.current;
          if (currentSelectedVet && dateStr) {
            setIsLoadingAvailability(true);
            setApiAvailabilityData(null);

            clinicAPI.getVetAvailability(currentSelectedVet.user_id, dateStr, dateStr)
              .then((response) => {
                const apiData = response.data?.data || response.data || null;
                setApiAvailabilityData(apiData);
              })
              .catch((error) => {
                console.error('Error fetching vet availability:', error);
                setApiAvailabilityData(null);
              })
              .finally(() => {
                setIsLoadingAvailability(false);
              });
          } else {
            setApiAvailabilityData(null);
          }
        }
      });
    }

    return () => {
      if (datePickerRef.current) {
        datePickerRef.current.destroy();
      }
    };
  }, [isOpen, workingHours]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen && !isInitializedRef.current) {
      // Modal just opened, initialize form
      setNewAppointment({
        id: null,
        pet_owner: "",
        pet: "",
        vet: "",
        reason: "",
        date: "",
        time: "",
        type: "",
      });

      // Set initial values if provided
      if (initialPetOwner) {
        setSelectedPetOwner(initialPetOwner);
        setPetOwnerSearch(initialPetOwner.display_name || initialPetOwner.full_name || `${initialPetOwner.first_name} ${initialPetOwner.last_name}` || "");
      } else {
        setPetOwnerSearch("");
        setSelectedPetOwner(null);
      }

      // Set initial pet if provided (for SOS conversations) - SET IT IMMEDIATELY
      if (initialPet) {
        setSelectedPet(initialPet);
      } else {
        setSelectedPet(null);
      }

      if (initialVet) {
        setSelectedVet(initialVet);
        setVetSearch(initialVet.user?.name || initialVet.name || "");
      } else {
        setVetSearch("");
        setSelectedVet(null);
      }

      // Don't clear pets if we have an initial pet owner - let the useEffect fetch them
      if (!initialPetOwner) {
        setPetOwnerPets([]);
        setIsLoadingPets(false);
      }
      // If initialPetOwner is provided, the useEffect will fetch pets automatically
      setPetOwnerSearchResults([]);
      setShowPetOwnerDropdown(false);
      setIsSearchingPetOwners(false);
      setShowVetDropdown(false);
      setShowTimeDropdown(false);
      setIsLoadingAvailability(false);
      setApiAvailabilityData(null);
      setIsSaving(false);

      // Mark as initialized
      isInitializedRef.current = true;
    } else if (!isOpen && isInitializedRef.current) {
      // Modal just closed, reset the flag
      isInitializedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSave = async () => {
    if (!selectedPetOwner) {
      showErrorToast(t("receptionAppointments.create.toasts.petGuardianRequired"));
      return;
    }
    if (!selectedPet) {
      showErrorToast(t("receptionAppointments.create.toasts.petRequired"));
      return;
    }
    if (!selectedVet) {
      showErrorToast(t("receptionAppointments.create.toasts.vetRequired"));
      return;
    }
    if (!newAppointment.date) {
      showErrorToast(t("receptionAppointments.create.toasts.dateRequired"));
      return;
    }
    if (!newAppointment.time) {
      showErrorToast(t("receptionAppointments.create.toasts.timeRequired"));
      return;
    }
    if (!newAppointment.type) {
      showErrorToast(t("receptionAppointments.create.toasts.typeRequired"));
      return;
    }

    setIsSaving(true);
    try {
      const appointment = {
        id: newAppointment.id || Date.now(),
        pet_owner_id: selectedPetOwner ? selectedPetOwner.id : null,
        pet_id: selectedPet ? selectedPet.id : null,
        vet_id: selectedVet ? selectedVet.user_id : null,
        reason: newAppointment.reason,
        date: newAppointment.date,
        time: newAppointment.time,
        appointment_type_id: newAppointment.type,
      };
      const success = await onSave(appointment);
      if (success) {
        showSuccessToast(t("receptionAppointments.create.toasts.saveSuccess"));
        onClose(); // Only close modal on successful save
      }
    } catch (error) {
      console.error("Error saving appointment:", error);
      showErrorToast(t("receptionAppointments.create.toasts.saveFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  // Add Escape key handler
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div
        className="reception-modal-overlay"
        dir={i18n.dir()}
        lang={i18n.language}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          backdropFilter: 'blur(4px)'
        }}
        onClick={e => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div
          className="reception-modal"
          style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}
          onClick={e => e.stopPropagation()}
        >
          <div className="reception-modal-header" style={{
            padding: '0 32px 16px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '24px',
              fontWeight: '600',
              color: '#111827'
            }}>
              {isAppointments
                ? t("receptionAppointments.create.title")
                : t("receptionAppointments.create.titleOutpatient")}
            </h2>
            <button
              className="reception-modal-close"
              onClick={onClose}
              aria-label={t("common.buttons.close")}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#6b7280',
                padding: '8px',
                borderRadius: '8px',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f3f4f6';
                e.target.style.color = '#374151';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#6b7280';
              }}
            >
              ✕
            </button>
          </div>
          <form
            className="reception-modal-form"
            onSubmit={(e) => {
              e.preventDefault();
            }}
            style={{
              paddingLeft: '32px',
              paddingRight: '32px'
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <label className="reception-modal-label" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  👤 {t("receptionAppointments.create.fields.petGuardian.label")}
                </span>
                <div style={{ position: 'relative' }}>
                  {/* Dropdown Button */}
                  <div
                    className="reception-modal-input"
                    onClick={() => setShowPetOwnerDropdown(!showPetOwnerDropdown)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '16px',
                      transition: 'all 0.2s',
                      outline: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: 'white'
                    }}
                  >
                    <span style={{ color: selectedPetOwner ? '#111827' : '#9ca3af' }}>
                      {selectedPetOwner
                        ? selectedPetOwner.display_name || selectedPetOwner.full_name || `${selectedPetOwner.first_name} ${selectedPetOwner.last_name}`
                        : t("receptionAppointments.create.fields.petGuardian.label")
                      }
                    </span>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      {showPetOwnerDropdown ? '▲' : '▼'}
                    </span>
                  </div>

                  {/* Dropdown Content */}
                  {showPetOwnerDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      zIndex: 10,
                      maxHeight: '300px',
                      overflow: 'hidden',
                      marginTop: '4px'
                    }}>
                      {/* Search Input Inside Dropdown */}
                      <div style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
                        <input
                          type="text"
                          placeholder={t("receptionAppointments.create.fields.petGuardian.searchPlaceholder")}
                          value={petOwnerSearch}
                          onChange={(e) => {
                            setPetOwnerSearch(e.target.value);
                            // Clear selected Pet Guardian when user starts typing to search for a new one
                            if (selectedPetOwner && e.target.value) {
                              setSelectedPetOwner(null);
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            outline: 'none'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>

                      {/* Results List */}
                      <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                        {isSearchingPetOwners ? (
                          <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6b7280'
                          }}>
                            <div style={{
                              display: 'inline-block',
                              width: '20px',
                              height: '20px',
                              border: '2px solid #e5e7eb',
                              borderTop: '2px solid #3b82f6',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite'
                            }}></div>
                            <div style={{ marginTop: '8px', fontSize: '14px' }}>
                              {t("receptionAppointments.create.search.searching")}
                            </div>
                          </div>
                        ) : petOwnerSearchResults.length > 0 ? (
                          petOwnerSearchResults.map((g) => (
                            <div
                              key={g.id}
                              style={{
                                cursor: "pointer",
                                padding: "12px 16px",
                                borderBottom: '1px solid #f3f4f6',
                                transition: 'background-color 0.2s'
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setSelectedPetOwner(g);
                                setShowPetOwnerDropdown(false);
                                setPetOwnerSearchResults([]);
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#f9fafb';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                              }}
                            >
                              <div>
                                <div style={{ fontWeight: '500', color: '#111827' }}>
                                  {g.display_name || g.full_name || `${g.first_name} ${g.last_name}` || fallbackLabels.petGuardian}
                                </div>
                                {g.last_completed_appointment_relation && (
                                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                                    {t("receptionAppointments.create.fields.lastVisit.dropdown", {
                                      date: new Date(g.last_completed_appointment_relation.appointment_at).toLocaleDateString(locale),
                                      status: g.last_completed_appointment_relation.status,
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))
                        ) : petOwnerSearch.trim() ? (
                          <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6b7280',
                            fontSize: '14px'
                          }}>
                            {t("receptionAppointments.create.fields.petGuardian.empty")}
                          </div>
                        ) : (
                          <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6b7280',
                            fontSize: '14px'
                          }}>
                            {t("receptionAppointments.create.fields.petGuardian.hint")}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </label>
              <label className="reception-modal-label" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  🐾 {t("receptionAppointments.create.fields.pet.label")}
                </span>
                <select
                  className="reception-modal-input"
                  value={selectedPet ? String(selectedPet.id) : ""}
                  onChange={(e) => {
                    const selectedPetId = e.target.value;
                    const pet = petOwnerPets.find(p => String(p.id) === selectedPetId);
                    setSelectedPet(pet || null);
                  }}
                  disabled={!selectedPetOwner || isLoadingPets || petOwnerPets.length === 0}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    transition: 'all 0.2s',
                    outline: 'none',
                    backgroundColor: (!selectedPetOwner || isLoadingPets) ? '#f9fafb' : 'white',
                    cursor: (!selectedPetOwner || isLoadingPets) ? 'not-allowed' : 'pointer'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="">
                    {!selectedPetOwner
                      ? t("receptionAppointments.create.fields.pet.placeholder")
                      : isLoadingPets
                        ? t("receptionAppointments.create.fields.pet.loading")
                        : petOwnerPets.length === 0
                          ? t("receptionAppointments.create.fields.pet.empty")
                          : t("receptionAppointments.create.fields.pet.placeholder")
                    }
                  </option>
                  {!isLoadingPets && petOwnerPets.map((p) => (
                    <option key={p.id} value={String(p.id)}>
                      {p.name}
                    </option>
                  ))}
                </select>
                {isLoadingPets && (
                  <div style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    pointerEvents: 'none'
                  }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #e5e7eb',
                      borderTop: '2px solid #3b82f6',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    <span style={{
                      fontSize: '12px',
                      color: '#6b7280',
                      fontWeight: '500'
                    }}>
                      {t("receptionAppointments.create.search.loading")}
                    </span>
                  </div>
                )}
              </label>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <label className="reception-modal-label" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  👨‍⚕️ {t("receptionAppointments.create.fields.vet.label")}
                </span>
                <div style={{ position: 'relative' }}>
                  {/* Dropdown Button */}
                  <div
                    className="reception-modal-input"
                    onClick={() => setShowVetDropdown(!showVetDropdown)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '16px',
                      transition: 'all 0.2s',
                      outline: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: 'white'
                    }}
                  >
                    <span style={{ color: selectedVet ? '#111827' : '#9ca3af' }}>
                      {selectedVet
                        ? selectedVet.user?.name || selectedVet.name || fallbackLabels.vet
                        : t("receptionAppointments.create.fields.vet.label")
                      }
                    </span>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      {showVetDropdown ? '▲' : '▼'}
                    </span>
                  </div>

                  {/* Dropdown Content */}
                  {showVetDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      zIndex: 10,
                      maxHeight: '300px',
                      overflow: 'hidden',
                      marginTop: '4px'
                    }}>
                      {/* Search Input Inside Dropdown */}
                      <div style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
                        <input
                          type="text"
                          placeholder={t("receptionAppointments.create.fields.vet.searchPlaceholder")}
                          value={vetSearch}
                          onChange={(e) => setVetSearch(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            outline: 'none'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>

                      {/* Results List */}
                      <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                        {vets.filter((v) => {
                          const displayName = getVetDisplayName(v);
                          return displayName && displayName.toLowerCase().includes(vetSearch.toLowerCase());
                        }).length > 0 ? (
                          vets
                            .filter((v) => {
                              const displayName = getVetDisplayName(v);
                              return displayName && displayName.toLowerCase().includes(vetSearch.toLowerCase());
                            })
                            .map((v) => {
                              const displayName = getVetDisplayName(v);
                              return (
                                <div
                                  key={v.user_id}
                                  style={{
                                    cursor: "pointer",
                                    padding: "12px 16px",
                                    borderBottom: '1px solid #f3f4f6',
                                    transition: 'background-color 0.2s'
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSelectedVet(v);
                                    setVetSearch(displayName);
                                    setShowVetDropdown(false);
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#f9fafb';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  {displayName}
                                </div>
                              );
                            })
                        ) : vetSearch.trim() ? (
                          <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6b7280',
                            fontSize: '14px'
                          }}>
                            {t("receptionAppointments.create.fields.vet.empty")}
                          </div>
                        ) : (
                          <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6b7280',
                            fontSize: '14px'
                          }}>
                            {t("receptionAppointments.create.fields.vet.hint")}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </label>
              <label className="reception-modal-label" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  📝 {t("receptionAppointments.create.fields.reason.label")}
                </span>
                <input
                  type="text"
                  className="reception-modal-input"
                  value={newAppointment.reason}
                  onChange={(e) =>
                    setNewAppointment((a) => ({
                      ...a,
                      reason: e.target.value,
                    }))
                  }
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    transition: 'all 0.2s',
                    outline: 'none'
                  }}
                  placeholder={t("receptionAppointments.create.fields.reason.label")}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </label>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '' }}>
              <label className="reception-modal-label" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  📅 {isAppointments
                    ? t("receptionAppointments.create.fields.date.label")
                    : t("receptionAppointments.create.fields.dateOutpatient.label")}
                </span>
                <input
                  ref={dateInputRef}
                  type="text"
                  className="reception-modal-input"
                  placeholder={t("receptionAppointments.create.fields.date.placeholder")}
                  readOnly
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    transition: 'all 0.2s',
                    outline: 'none',
                    cursor: 'pointer',
                    backgroundColor: 'white'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </label>
              <label className="reception-modal-label" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151'
                  }}>
                    ⏰ {isAppointments
                      ? t("receptionAppointments.create.fields.time.label")
                      : t("receptionAppointments.create.fields.timeOutpatient.label")}
                  </span>
                  {apiAvailabilityData && apiAvailabilityData.slot_duration_minutes && (
                    <span style={{
                      fontSize: '12px',
                      color: '#6b7280',
                      backgroundColor: '#f3f4f6',
                      padding: '2px 6px',
                      borderRadius: '4px'
                    }}>
                      {t("receptionAppointments.create.fields.time.slotDuration", {
                        minutes: apiAvailabilityData.slot_duration_minutes,
                      })}
                    </span>
                  )}
                </div>
                <div style={{ position: 'relative' }}>
                  {/* Time Dropdown Button */}
                  <div
                    className="reception-modal-input"
                    onClick={() => {
                      if (isLoadingAvailability) return;
                      const availableSlots = getAvailableTimeSlotsForDate(newAppointment.date);
                      if (availableSlots.length > 0) {
                        setShowTimeDropdown(!showTimeDropdown);
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '16px',
                      transition: 'all 0.2s',
                      outline: 'none',
                      cursor: isLoadingAvailability ? 'not-allowed' : (getAvailableTimeSlotsForDate(newAppointment.date).length > 0 ? 'pointer' : 'not-allowed'),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: isLoadingAvailability || getAvailableTimeSlotsForDate(newAppointment.date).length === 0 ? '#f9fafb' : 'white'
                    }}
                  >
                    <span style={{ color: newAppointment.time ? '#111827' : '#9ca3af' }}>
                      {isLoadingAvailability
                        ? t("receptionAppointments.create.fields.time.loading")
                        : (newAppointment.time || t("receptionAppointments.create.fields.time.placeholder"))}
                    </span>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      {isLoadingAvailability ? (
                        <div style={{
                          display: 'inline-block',
                          width: '16px',
                          height: '16px',
                          border: '2px solid #e5e7eb',
                          borderTop: '2px solid #3b82f6',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></div>
                      ) : (
                        showTimeDropdown ? '▲' : '▼'
                      )}
                    </span>
                  </div>

                  {/* Time Dropdown Content */}
                  {showTimeDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      zIndex: 10,
                      maxHeight: '200px',
                      overflow: 'auto',
                      marginTop: '4px'
                    }}>
                      {getAvailableTimeSlotsForDate(newAppointment.date).length > 0 ? (
                        getAvailableTimeSlotsForDate(newAppointment.date).map((slot, index) => (
                          <div
                            key={index}
                            style={{
                              cursor: "pointer",
                              padding: "12px 16px",
                              borderBottom: '1px solid #f3f4f6',
                              transition: 'background-color 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              backgroundColor: slot.time === newAppointment.time ? '#e0f2fe' : 'transparent',
                              borderLeft: slot.time === newAppointment.time ? '3px solid #3b82f6' : '3px solid transparent',
                              color: slot.time === newAppointment.time ? '#1d4ed8' : '#111827',
                              fontWeight: slot.time === newAppointment.time ? '600' : '400'
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setNewAppointment(prev => ({ ...prev, time: slot.time }));
                              setShowTimeDropdown(false);
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#f9fafb';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'transparent';
                            }}
                          >
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                              <span style={{ fontSize: '16px', fontWeight: '500' }}>
                                {slot.display}
                              </span>
                              {slot.endTime && (
                                <span style={{
                                  fontSize: '12px',
                                  color: '#6b7280'
                                }}>
                                  - {slot.endTime}
                                </span>
                              )}
                            </div>
                            <span style={{
                              fontSize: '12px',
                              color: '#6b7280',
                              backgroundColor: '#f3f4f6',
                              padding: '2px 8px',
                              borderRadius: '4px'
                            }}>
                              {slot.day}
                            </span>
                          </div>
                        ))
                      ) : (
                        <div style={{
                          padding: '20px',
                          textAlign: 'center',
                          color: '#6b7280',
                          fontSize: '14px'
                        }}>
                          {newAppointment.date ?
                            (() => {
                              if (apiAvailabilityData && apiAvailabilityData.availability) {
                                const availability = apiAvailabilityData.availability;
                                if (!availability.is_clinic_open) {
                                  return t("receptionAppointments.create.fields.time.closed");
                                }
                                if (availability.available_count === 0) {
                                  return t("receptionAppointments.create.fields.time.noSlotsWithCount", {
                                    booked: availability.booked_count || 0,
                                  });
                                }
                              }

                              const selectedDateObj = new Date(newAppointment.date);
                              const today = new Date();
                              const isSelectedDateToday = selectedDateObj.toDateString() === today.toDateString();
                              return isSelectedDateToday
                                ? t("receptionAppointments.create.fields.time.noSlotsToday")
                                : t("receptionAppointments.create.fields.time.noSlotsDay");
                            })() :
                            t("receptionAppointments.create.fields.time.selectDateFirst")
                          }
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </label>
              <label className="reception-modal-label" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  🕒 {t("receptionAppointments.create.fields.lastVisit.label")}
                </span>
                <input
                  type="text"
                  className="reception-modal-input"
                  value={selectedPetOwner?.last_completed_appointment_relation
                    ? t("receptionAppointments.create.fields.lastVisit.value", {
                      date: new Date(selectedPetOwner.last_completed_appointment_relation.appointment_at).toLocaleDateString(locale),
                      status: selectedPetOwner.last_completed_appointment_relation.status,
                    })
                    : fallbackLabels.noVisits
                  }
                  readOnly
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    transition: 'all 0.2s',
                    outline: 'none',
                    backgroundColor: '#f9fafb',
                    color: '#6b7280'
                  }}
                />
              </label>
              <label className="reception-modal-label" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  {t("receptionAppointments.create.fields.type.label")}
                </span>
                <select
                  className="reception-modal-input"
                  value={newAppointment.type || ""}
                  onChange={(e) =>
                    setNewAppointment((a) => ({
                      ...a,
                      type: e.target.value,
                    }))
                  }
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    transition: 'all 0.2s',
                    outline: 'none',
                    backgroundColor: 'white',
                    color: newAppointment.type ? '#111827' : '#9ca3af'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="" disabled>
                    {t("receptionAppointments.create.fields.type.placeholder")}
                  </option>
                  {appointmentTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.title}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="reception-modal-actions" style={{
              display: 'flex',
              gap: '16px',
              justifyContent: 'flex-end',
              paddingTop: '10px',
              borderTop: '1px solid #e5e7eb',
              marginTop: '15px'
            }}>
              <button
                type="button"
                className="reception-action-btn"
                onClick={onClose}
                style={{
                  padding: '12px 24px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.backgroundColor = 'white';
                }}
              >
                {t("receptionAppointments.create.buttons.cancel")}
              </button>
              <button
                type="button"
                className="reception-action-btn"
                onClick={handleSave}
                disabled={isSaving}
                style={{
                  width: '250px',
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: isSaving ? '#9ca3af' : '#3b82f6',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                  opacity: isSaving ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  if (!isSaving) {
                    e.target.style.backgroundColor = '#2563eb';
                    e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSaving) {
                    e.target.style.backgroundColor = '#3b82f6';
                    e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                  }
                }}
              >
                {isSaving && (
                  <div style={{
                    display: 'inline-block',
                    width: '16px',
                    height: '16px',
                    border: '2px solid #ffffff',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                )}
                {isSaving
                  ? t("receptionAppointments.create.buttons.saving")
                  : t("receptionAppointments.create.buttons.save")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AppointmentModal;
